import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Store } from 'lucide-react';
import { toast } from 'sonner';
import { compressImage, formatCompressionStats } from '../../utils/imageCompression';
import { ImageWithFallback } from '../ui/image-with-fallback';

/**
 * Square business-logo uploader + previewer used on the approved-merchant
 * dashboard and the pending/rejected resubmission form. Compresses to 512px
 * before upload, persists the URL via `PUT /api/merchant/profile`.
 */
export function BusinessLogoCard({ api, currentUrl, onUpdated, businessName = '' }) {
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const pick = () => fileRef.current?.click();

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const before = f.size;
      const compressed = await compressImage(f, { maxSide: 512, quality: 0.85 });
      if (compressed.size < before) {
        toast.message(`Logo compressed: ${formatCompressionStats(before, compressed.size)}`);
      }
      const fd = new FormData();
      fd.append('file', compressed);
      fd.append('purpose', 'merchant-logo');
      if (businessName) fd.append('subject_name', businessName);
      const up = await api.post('/api/upload/member-file', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = up.data.url || up.data.file_url;
      await api.put('/api/merchant/profile', { business_logo_url: url });
      toast.success('Business logo updated');
      await onUpdated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload logo');
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const remove = async () => {
    setUploading(true);
    try {
      await api.put('/api/merchant/profile', { business_logo_url: '' });
      toast.success('Logo removed');
      await onUpdated?.();
    } catch (err) {
      toast.error('Failed to remove logo');
    }
    setUploading(false);
  };

  return (
    <Card className="bg-[#041A3B] border-white/10 mb-8" data-testid="business-logo-card">
      <CardContent className="p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl bg-[#000B18] border border-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
          {currentUrl
            ? <ImageWithFallback
                src={currentUrl}
                alt="Business logo"
                className="w-full h-full object-cover"
                fallback={<Store className="w-10 h-10 text-[#5C7599]" data-testid="business-logo-fallback" />}
              />
            : <Store className="w-10 h-10 text-[#5C7599]" />
          }
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-outfit font-semibold text-base sm:text-lg mb-1">Business Logo</h3>
          <p className="text-[#A0B4CC] text-xs sm:text-sm">
            Shown on your marketplace listing and partner profile.
            <span className="block text-[#5C7599] text-[11px] mt-1">Recommended: 512×512 px square · PNG or JPG · auto-compressed on upload</span>
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
          <Button onClick={pick} disabled={uploading} className="btn-primary h-10 flex-1 sm:flex-none" data-testid="upload-logo-btn">
            {uploading ? '…' : currentUrl ? 'Replace' : 'Upload'}
          </Button>
          {currentUrl && (
            <Button onClick={remove} disabled={uploading} variant="outline" className="border-white/20 text-[#A0B4CC] h-10" data-testid="remove-logo-btn">
              Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
